// Scroll-snap gallery: arrows, thumbs and keys scroll the track; the observer keeps the meta in sync.
// The zoom button opens the section's own dialog with the active image.
class UsdcHeroGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-gallery-track]');
    if (!this.track) return;

    this.slides = [...this.querySelectorAll('[data-gallery-slide]')];
    this.thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];
    this.thumbList = this.querySelector('[data-gallery-thumbs]');
    this.label = this.querySelector('[data-gallery-label]');
    this.counter = this.querySelector('[data-gallery-counter]');
    this.note = this.querySelector('[data-gallery-note]');
    this.dialog = this.querySelector('dialog');
    this.dialogImage = this.querySelector('[data-dialog-image]');
    this.dialogCaption = this.querySelector('[data-dialog-caption]');
    this.index = 0;

    this.handleClick = this.handleClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.addEventListener('click', this.handleClick);
    this.track.addEventListener('keydown', this.handleKeydown);

    this.observer = new IntersectionObserver(this.handleIntersect.bind(this), {
      root: this.track,
      threshold: 0.6,
    });
    for (const slide of this.slides) this.observer.observe(slide);

    this.update(0);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    this.track?.removeEventListener('keydown', this.handleKeydown);
    this.observer?.disconnect();
  }

  get scrollBehavior() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  }

  handleClick(event) {
    if (event.target.closest('[data-gallery-zoom]')) {
      this.openDialog();
      return;
    }

    if (event.target === this.dialog || event.target.closest('[data-dialog-close]')) {
      this.dialog.close();
      return;
    }

    const thumb = event.target.closest('[data-gallery-thumb]');
    if (thumb) {
      this.goTo(Number(thumb.dataset.index));
      return;
    }

    const arrow = event.target.closest('[data-gallery-step]');
    if (arrow) this.goTo(this.index + Number(arrow.dataset.galleryStep));
  }

  handleKeydown(event) {
    const steps = { ArrowRight: 1, ArrowLeft: -1 };
    const step = steps[event.key];
    if (!step) return;
    event.preventDefault();
    this.goTo(this.index + step);
  }

  handleIntersect(entries) {
    const visible = entries.find(({ isIntersecting }) => isIntersecting);
    if (visible) this.update(this.slides.indexOf(visible.target));
  }

  goTo(index) {
    const count = this.slides.length;
    const next = (index + count) % count;
    this.track.scrollTo({ left: this.slides[next].offsetLeft, behavior: this.scrollBehavior });
    this.update(next);
  }

  update(index) {
    const slide = this.slides[index];
    if (!slide) return;

    this.index = index;
    const { label, note } = slide.dataset;
    const position = `${index + 1} / ${this.slides.length}`;

    this.label.textContent = label;
    this.counter.textContent = position;
    this.note.textContent = note;

    this.thumbs.forEach((thumb, thumbIndex) => {
      thumb.setAttribute('aria-current', String(thumbIndex === index));
    });

    const thumb = this.thumbs[index];
    if (thumb && this.thumbList) {
      this.thumbList.scrollTo({ left: thumb.offsetLeft - 12, behavior: this.scrollBehavior });
    }
  }

  openDialog() {
    const { label, zoomSrc, zoomAlt = '' } = this.slides[this.index].dataset;
    const position = `${this.index + 1} / ${this.slides.length}`;

    this.dialogImage.src = zoomSrc;
    this.dialogImage.alt = zoomAlt;
    this.dialogCaption.textContent = label ? `${position} · ${label}` : position;
    this.dialog.showModal();
  }
}

if (!customElements.get('usdc-hero-gallery')) {
  customElements.define('usdc-hero-gallery', UsdcHeroGallery);
}
